/**
 * Live Collaboration Panel
 * Real-time collaboration UI with user presence, chat, and activity feed
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  Users,
  Send,
  MessageCircle,
  Circle,
  X,
  Link2,
  Copy,
  Check,
  Wifi,
  WifiOff,
  ChevronDown,
  ChevronUp,
  UserPlus,
  Eye,
  LogOut,
} from 'lucide-react';
import { toast } from 'sonner';
import { ActivityFeed, useActivityFeed } from './ActivityFeed';

// Get WebSocket URL from environment
const getWsUrl = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  // Convert http(s) to ws(s)
  return backendUrl.replace(/^http/, 'ws');
};

// User avatar component
const UserAvatar = ({ user, size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-6 h-6 text-xs',
    md: 'w-8 h-8 text-sm',
    lg: 'w-10 h-10 text-base',
  };
  
  return (
    <div
      className={`${sizeClasses[size]} rounded-full flex items-center justify-center font-medium text-white`}
      style={{ backgroundColor: user.color }}
      title={user.name}
    >
      {user.name?.charAt(0)?.toUpperCase() || '?'}
    </div>
  );
};

// Online users list
const OnlineUsers = ({ users, currentUserId }) => {
  if (users.length === 0) {
    return (
      <div className="text-center py-4 text-zinc-500 text-sm">
        No other users online
      </div>
    );
  }
  
  return (
    <div className="space-y-2" data-testid="online-users-list">
      {users.map((user) => (
        <div
          key={user.id}
          className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
            user.id === currentUserId 
              ? 'bg-indigo-500/10 border border-indigo-500/30' 
              : 'bg-zinc-800/50 hover:bg-zinc-800'
          }`}
          data-testid={`user-${user.id}`}
        >
          <UserAvatar user={user} />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-white truncate">
              {user.name}
              {user.id === currentUserId && (
                <span className="text-xs text-indigo-400 ml-2">(you)</span>
              )}
            </div>
            {user.selectedTrack && (
              <div className="text-xs text-zinc-400 flex items-center gap-1">
                <Eye className="w-3 h-3" />
                Editing track
              </div>
            )}
          </div>
          <Circle className="w-2 h-2 fill-emerald-400 text-emerald-400 animate-pulse" />
        </div>
      ))}
    </div>
  );
};

// Chat message component
const ChatMessage = ({ message, isOwn }) => {
  return (
    <div className={`flex gap-2 ${isOwn ? 'flex-row-reverse' : ''}`} data-testid="chat-message">
      <UserAvatar 
        user={{ name: message.userName, color: message.userColor || '#6366f1' }} 
        size="sm" 
      />
      <div className={`max-w-[70%] ${isOwn ? 'text-right' : ''}`}>
        <div className="text-xs text-zinc-400 mb-0.5">
          {message.userName}
          <span className="mx-1">·</span>
          {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
        <div
          className={`px-3 py-1.5 rounded-lg text-sm ${
            isOwn 
              ? 'bg-indigo-500 text-white' 
              : 'bg-zinc-800 text-zinc-200'
          }`}
        >
          {message.message}
        </div>
      </div>
    </div>
  );
};

// Main Collaboration Panel
export const CollaborationPanel = ({
  projectId,
  userId,
  userName,
  onUserCursorMove,
  onTrackSelect,
  onRemoteUpdate,
  onUsersUpdate,
  onClose,
  onLeaveSession,
  isVisible = true,
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [showChat, setShowChat] = useState(true);
  const [showActivity, setShowActivity] = useState(true);
  const [copied, setCopied] = useState(false);
  
  // Activity feed hook
  const { activities, addActivity, clearActivities } = useActivityFeed(50);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const isIntentionalCloseRef = useRef(false); // Flag to suppress warnings on intentional disconnect

  // Connect to WebSocket
  const connect = useCallback(() => {
    // Don't connect if already open or connecting
    if (wsRef.current?.readyState === WebSocket.OPEN || 
        wsRef.current?.readyState === WebSocket.CONNECTING) return;
    
    setIsConnecting(true);
    
    const wsUrl = `${getWsUrl()}/api/collaboration/ws/${projectId}/${userId}?user_name=${encodeURIComponent(userName)}`;
    
    try {
      const ws = new WebSocket(wsUrl);
      
      ws.onopen = () => {
        setIsConnected(true);
        setIsConnecting(false);
        toast.success('Connected to collaboration session');
      };
      
      ws.onclose = () => {
        setIsConnected(false);
        setIsConnecting(false);
        
        // Only auto-reconnect if this wasn't an intentional close
        if (!isIntentionalCloseRef.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            if (isVisible) connect();
          }, 3000);
        }
        // Reset the flag for future connections
        isIntentionalCloseRef.current = false;
      };
      
      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setIsConnecting(false);
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleMessage(data);
        } catch (err) {
          console.error('Error parsing message:', err);
        }
      };
      
      wsRef.current = ws;
      
    } catch (err) {
      console.error('Failed to connect:', err);
      setIsConnecting(false);
    }
  }, [projectId, userId, userName, isVisible]);

  // Handle incoming messages
  const handleMessage = useCallback((data) => {
    switch (data.type) {
      case 'init':
        setUsers(data.users || []);
        onUsersUpdate?.(data.users || []);
        break;
        
      case 'user_joined':
        setUsers(data.users || []);
        onUsersUpdate?.(data.users || []);
        toast.info(`${data.user?.name || 'Someone'} joined the session`);
        addActivity({
          type: 'user_joined',
          userName: data.user?.name || 'Someone',
          userColor: data.user?.color,
        });
        break;
        
      case 'user_left':
        setUsers(data.users || []);
        onUsersUpdate?.(data.users || []);
        addActivity({
          type: 'user_left',
          userName: data.userName || 'Someone',
          userColor: data.userColor,
        });
        break;
        
      case 'cursor_update':
        onUserCursorMove?.(data.userId, data.cursor);
        break;
        
      case 'track_selection':
        setUsers(prev => prev.map(u => 
          u.id === data.userId ? { ...u, selectedTrack: data.trackId } : u
        ));
        break;
      
      // Handle operation applied messages (from collaboration_v2.py)
      case 'track_add_applied':
        onRemoteUpdate?.(data);
        addActivity({
          type: 'track_add',
          userName: data.operation?.userName || 'Someone',
          userColor: data.operation?.userColor,
          details: data.operation?.data?.track?.name,
        });
        break;
        
      case 'track_delete_applied':
        onRemoteUpdate?.(data);
        addActivity({
          type: 'track_delete',
          userName: data.operation?.userName || 'Someone',
          userColor: data.operation?.userColor,
        });
        break;
        
      case 'track_update_applied':
        onRemoteUpdate?.(data);
        addActivity({
          type: 'track_update',
          userName: data.operation?.userName || 'Someone',
          userColor: data.operation?.userColor,
        });
        break;
        
      case 'clip_add_applied':
        onRemoteUpdate?.(data);
        addActivity({
          type: 'clip_add',
          userName: data.operation?.userName || 'Someone',
          userColor: data.operation?.userColor,
        });
        break;
        
      case 'clip_move_applied':
        onRemoteUpdate?.(data);
        addActivity({
          type: 'clip_move',
          userName: data.operation?.userName || 'Someone',
          userColor: data.operation?.userColor,
        });
        break;
        
      case 'clip_delete_applied':
        onRemoteUpdate?.(data);
        addActivity({
          type: 'clip_delete',
          userName: data.operation?.userName || 'Someone',
          userColor: data.operation?.userColor,
        });
        break;
        
      case 'plugin_add_applied':
        onRemoteUpdate?.(data);
        addActivity({
          type: 'plugin_add',
          userName: data.operation?.userName || 'Someone',
          userColor: data.operation?.userColor,
          details: data.operation?.data?.plugin?.name,
        });
        break;
        
      case 'plugin_param_change_applied':
        onRemoteUpdate?.(data);
        addActivity({
          type: 'plugin_param_change',
          userName: data.operation?.userName || 'Someone',
          userColor: data.operation?.userColor,
        });
        break;
        
      case 'bpm_change_applied':
        onRemoteUpdate?.(data);
        addActivity({
          type: 'bpm_change',
          userName: data.operation?.userName || 'Someone',
          userColor: data.operation?.userColor,
          details: `to ${data.operation?.data?.bpm} BPM`,
        });
        break;
      
      case 'time_signature_change_applied':
        onRemoteUpdate?.(data);
        addActivity({
          type: 'time_signature_change',
          userName: data.operation?.userName || 'Someone',
          userColor: data.operation?.userColor,
          details: `to ${data.operation?.data?.timeSignature}/4`,
        });
        break;
        
      case 'transport_update':
        onRemoteUpdate?.(data);
        const transportType = data.action === 'play' ? 'transport_play' 
          : data.action === 'pause' ? 'transport_pause' 
          : 'transport_stop';
        addActivity({
          type: transportType,
          userName: data.userName || 'Someone',
          userColor: data.userColor,
        });
        break;
        
      // Legacy message types
      case 'track_updated':
      case 'clip_moved':
      case 'clip_added':
      case 'clip_deleted':
      case 'bpm_changed':
      case 'chords_updated':
      case 'time_signature_changed':
        onRemoteUpdate?.(data);
        break;
        
      case 'chat_message':
        setMessages(prev => [...prev, {
          userId: data.userId,
          userName: data.userName,
          userColor: data.userColor,
          message: data.message,
          timestamp: data.timestamp,
        }]);
        break;
      
      // Handle conflict notifications
      case 'operation_conflict':
      case 'conflict':
        toast.warning('Edit conflict detected - your change was adjusted to merge with another collaborator\'s edit', {
          duration: 4000,
        });
        addActivity({
          type: 'conflict',
          userName: 'System',
          details: 'Edit conflict resolved automatically',
        });
        break;
      
      // Handle operation failures
      case 'operation_failed':
        if (data.reason?.includes('conflict')) {
          toast.error('Your edit was rejected due to a conflict. Please try again.', {
            duration: 4000,
          });
        }
        break;
        
      default:
        // Handle any other _applied messages
        if (data.type?.endsWith('_applied')) {
          onRemoteUpdate?.(data);
        } else if (data.success === false && data.reason?.includes('conflict')) {
          // Handle conflict from operation response
          toast.warning('Edit conflict - your change was merged with another edit', {
            duration: 3000,
          });
        } else {
        }
    }
  }, [onUserCursorMove, onRemoteUpdate, onUsersUpdate, addActivity]);

  // Send message via WebSocket
  const send = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);

  // Send cursor position
  const sendCursor = useCallback((cursor) => {
    send({
      type: 'cursor_move',
      cursor,
    });
  }, [send]);

  // Send track selection
  const sendTrackSelect = useCallback((trackId) => {
    send({
      type: 'track_select',
      trackId,
    });
  }, [send]);

  // Send transport state (play/pause/stop)
  const sendTransport = useCallback((action, position) => {
    send({
      type: 'transport',
      action, // 'play', 'pause', 'stop'
      position, // playhead position in seconds
    });
  }, [send]);

  // Send BPM change
  const sendBpmChange = useCallback((newBpm) => {
    send({
      type: 'bpm_change',
      data: { bpm: newBpm },
    });
  }, [send]);

  // Send Time Signature change
  const sendTimeSignatureChange = useCallback((newTimeSignature) => {
    send({
      type: 'time_signature_change',
      data: { timeSignature: newTimeSignature },
    });
  }, [send]);

  // Send chat message
  const sendChatMessage = () => {
    if (!newMessage.trim() || !isConnected) return;
    
    send({
      type: 'chat',
      userName,
      message: newMessage.trim(),
    });
    setNewMessage('');
  };

  // Copy invite link
  const copyInviteLink = () => {
    const url = `${window.location.origin}?project=${projectId}&collab=true`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Invite link copied!');
  };

  // Make collaboration functions available globally
  useEffect(() => {
    window.__collabSendCursor = sendCursor;
    window.__collabSendTrackSelect = sendTrackSelect;
    window.__collabSendTransport = sendTransport;
    window.__collabSendBpmChange = sendBpmChange;
    window.__collabSendTimeSignature = sendTimeSignatureChange;
    return () => {
      delete window.__collabSendCursor;
      delete window.__collabSendTrackSelect;
      delete window.__collabSendTransport;
      delete window.__collabSendBpmChange;
      delete window.__collabSendTimeSignature;
    };
  }, [sendCursor, sendTrackSelect, sendTransport, sendBpmChange, sendTimeSignatureChange]);

  // Auto-scroll chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Connect/disconnect on visibility change
  useEffect(() => {
    if (isVisible && !isConnected && !isConnecting) {
      connect();
    }
    
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [isVisible, connect, isConnected, isConnecting]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Mark as intentional close when component unmounts
      isIntentionalCloseRef.current = true;
      // Only close if WebSocket exists and is open/connecting
      if (wsRef.current && 
          (wsRef.current.readyState === WebSocket.OPEN || 
           wsRef.current.readyState === WebSocket.CONNECTING)) {
        wsRef.current.close();
      }
    };
  }, []);

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="w-80 bg-zinc-900/95 backdrop-blur-md border-l border-zinc-700 flex flex-col h-full"
      data-testid="collaboration-panel"
    >
      {/* Header */}
      <div className="px-4 py-3 border-b border-zinc-800 bg-gradient-to-r from-emerald-500/10 to-cyan-500/10">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-white">Live Collaboration</h3>
            <div className="flex items-center gap-1 text-xs">
              {isConnected ? (
                <>
                  <Wifi className="w-3 h-3 text-emerald-400" />
                  <span className="text-emerald-400">Connected</span>
                </>
              ) : isConnecting ? (
                <>
                  <Wifi className="w-3 h-3 text-amber-400 animate-pulse" />
                  <span className="text-amber-400">Connecting...</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-3 h-3 text-red-400" />
                  <span className="text-red-400">Disconnected</span>
                </>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-zinc-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      {/* Invite Link & Session Actions */}
      <div className="px-4 py-2 border-b border-zinc-800">
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={copyInviteLink}
            className="flex-1 border-zinc-700 text-zinc-300"
            data-testid="copy-invite-link-btn"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4 mr-2 text-emerald-400" />
                Copied!
              </>
            ) : (
              <>
                <Link2 className="w-4 h-4 mr-2" />
                Copy Link
              </>
            )}
          </Button>
          {onLeaveSession && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                // Mark as intentional close to prevent reconnect and warnings
                isIntentionalCloseRef.current = true;
                // Clear any pending reconnect timeout
                if (reconnectTimeoutRef.current) {
                  clearTimeout(reconnectTimeoutRef.current);
                  reconnectTimeoutRef.current = null;
                }
                // Close WebSocket connection if it exists and is open/connecting
                if (wsRef.current && 
                    (wsRef.current.readyState === WebSocket.OPEN || 
                     wsRef.current.readyState === WebSocket.CONNECTING)) {
                  wsRef.current.close();
                }
                onLeaveSession();
              }}
              className="border-red-500/50 text-red-400 hover:bg-red-500/20"
              data-testid="leave-session-btn"
              title="Leave Session"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          )}
        </div>
        <p className="text-xs text-zinc-500 mt-1.5 text-center">
          Session: <span className="font-mono text-zinc-400">{projectId}</span>
        </p>
      </div>
      
      {/* Online Users */}
      <div className="px-4 py-3 border-b border-zinc-800">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-white">
            Online ({users.length})
          </span>
          <UserPlus className="w-4 h-4 text-zinc-400" />
        </div>
        <OnlineUsers users={users} currentUserId={userId} />
      </div>
      
      {/* Chat Section */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <button
          onClick={() => setShowChat(!showChat)}
          className="px-4 py-2 flex items-center justify-between text-sm text-zinc-300 hover:bg-zinc-800/50 transition-colors"
        >
          <span className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4" />
            Chat
          </span>
          {showChat ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        
        <AnimatePresence>
          {showChat && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: 'auto' }}
              exit={{ height: 0 }}
              className="flex-1 flex flex-col overflow-hidden"
            >
              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-2 space-y-3">
                {messages.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500 text-sm">
                    No messages yet
                  </div>
                ) : (
                  messages.map((msg, index) => (
                    <ChatMessage
                      key={index}
                      message={msg}
                      isOwn={msg.userId === userId}
                    />
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>
              
              {/* Message Input */}
              <div className="p-3 border-t border-zinc-800">
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                    placeholder="Type a message..."
                    disabled={!isConnected}
                    className="flex-1 bg-zinc-800 border-zinc-700"
                    data-testid="chat-input"
                  />
                  <Button
                    size="sm"
                    onClick={sendChatMessage}
                    disabled={!isConnected || !newMessage.trim()}
                    className="bg-indigo-500 hover:bg-indigo-600"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      
      {/* Activity Feed */}
      <ActivityFeed
        activities={activities}
        isCollapsed={!showActivity}
        onToggleCollapse={() => setShowActivity(!showActivity)}
        onClear={clearActivities}
      />
      
      {/* Reconnect Button */}
      {!isConnected && !isConnecting && (
        <div className="p-4 border-t border-zinc-800">
          <Button
            onClick={connect}
            className="w-full bg-emerald-500 hover:bg-emerald-600"
            data-testid="reconnect-btn"
          >
            <Wifi className="w-4 h-4 mr-2" />
            Reconnect
          </Button>
        </div>
      )}
    </motion.div>
  );
};

export default CollaborationPanel;
